import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import type { IActivityLocation, IBoondock, IListing, IUser } from '@curbnturf/entities';
import { UserApiService } from '../backend-api/user-api.service';
import { ListingApiService } from '../backend-api/listing-api.service';
import { BoondockApiService } from '../backend-api/boondock-api.service';
import { ActivityApiService } from '../backend-api/activity-api.service';
import { AuthService } from '../auth/auth.service';
import { FavoriteKind, IFavorite, favoriteKey } from '../favorites.util';

/**
 * Real-backed favorites/wishlists — talks to curbnturf's favorite/unfavorite
 * endpoints on `listing`, `boondock`, and `activity-location` (POI), and
 * hydrates from `GET user/:id/favorites`, via the local forked API-client
 * services in `../backend-api/` (see that folder's header for why these are
 * forks of the real `@curbnturf/{listing,boondock-listing,activity,user}`
 * services rather than the real classes directly).
 *
 * Favoriting requires sign-in — there is no anonymous/local fallback.
 * Logged-out visitors are gated at the call site via `redirectToSignIn()`
 * (see `auth-redirect.util.ts`) before any method here is invoked.
 *
 * Known, deliberate gaps:
 *  - None of the three favorite-row entities have a timestamp column
 *    server-side — only an auto-increment `id`, already returned in the
 *    server's own insertion order. `savedAt` is left `''`; "newest first"
 *    is array order from the hydration response, not a real date.
 *  - No bulk clear/replace endpoint exists — `removeAll()` loops individual
 *    `unfavorite` calls, so it's best-effort, not atomic.
 */
@Injectable({ providedIn: 'root' })
export class FavoritesService {
  private auth = inject(AuthService);
  private userService = inject(UserApiService);
  private listingService = inject(ListingApiService);
  private boondockService = inject(BoondockApiService);
  private activityService = inject(ActivityApiService);

  private readonly _favorites$ = new BehaviorSubject<IFavorite[]>([]);
  readonly favorites$: Observable<IFavorite[]> = this._favorites$.asObservable();

  constructor() {
    this.auth.currentUser$.subscribe(user => {
      if (user?.id) {
        void this.refreshNow();
      } else {
        this._favorites$.next([]);
      }
    });
  }

  // ---- Hydration ----------------------------------------------------------

  async refreshNow(): Promise<void> {
    const userId = this.auth.currentUser?.id;
    if (!userId) return;
    try {
      const user = await firstValueFrom(this.userService.fetchFavorites(userId));
      this._favorites$.next(mapUserFavorites(user));
    } catch (err) {
      console.error('[favorites] failed to refresh', err);
    }
  }

  // ---- Public read API (sync — reads the in-memory cache) ----------------

  list(): IFavorite[] {
    return this._favorites$.value;
  }

  isFavorite(kind: FavoriteKind, id: number | string): boolean {
    const key = favoriteKey(kind, id);
    return this._favorites$.value.some(f => favoriteKey(f.kind, f.id) === key);
  }

  favoriteKeys(): Set<string> {
    return new Set(this._favorites$.value.map(f => favoriteKey(f.kind, f.id)));
  }

  /** BACK-COMPAT: numeric-id Set of stays (skips POIs) — the shape `/search`,
   *  listing-card, and listing-details previously read off `readFavoriteIds`. */
  favoriteIds(): Set<number> {
    const out = new Set<number>();
    for (const f of this._favorites$.value) {
      if (typeof f.id === 'number') out.add(f.id);
    }
    return out;
  }

  // ---- Mutations (async — real HTTP, optimistic cache update) ------------

  /** Returns `false` (rather than throwing) on failure, after rolling back the
   *  optimistic update, so fire-and-forget callers don't need a try/catch —
   *  callers that already have `ToastService` can await + surface an error. */
  async addFavorite(kind: FavoriteKind, id: number | string): Promise<boolean> {
    const userId = this.auth.currentUser?.id;
    if (!userId) return false;
    if (this.isFavorite(kind, id)) return true;
    const entry: IFavorite = { kind, id, savedAt: '' };
    this._favorites$.next([entry, ...this._favorites$.value]);
    try {
      await firstValueFrom(this.favoriteCall(kind, id, 'favorite'));
      return true;
    } catch (err) {
      console.error('[favorites] failed to add', kind, id, err);
      this._favorites$.next(this._favorites$.value.filter(f => f !== entry));
      return false;
    }
  }

  async removeFavorite(kind: FavoriteKind, id: number | string): Promise<boolean> {
    const userId = this.auth.currentUser?.id;
    if (!userId) return false;
    const key = favoriteKey(kind, id);
    const previous = this._favorites$.value;
    if (!previous.some(f => favoriteKey(f.kind, f.id) === key)) return true;
    this._favorites$.next(previous.filter(f => favoriteKey(f.kind, f.id) !== key));
    try {
      await firstValueFrom(this.favoriteCall(kind, id, 'unfavorite'));
      return true;
    } catch (err) {
      console.error('[favorites] failed to remove', kind, id, err);
      this._favorites$.next(previous);
      return false;
    }
  }

  /** Best-effort bulk remove (no atomic backend endpoint) — used by the
   *  wishlists "Clear all" flow. Returns the entries that failed to remove. */
  async removeAll(entries: IFavorite[]): Promise<IFavorite[]> {
    const failed: IFavorite[] = [];
    for (const f of entries) {
      const ok = await this.removeFavorite(f.kind, f.id);
      if (!ok) failed.push(f);
    }
    return failed;
  }

  private favoriteCall(kind: FavoriteKind, id: number | string, action: 'favorite' | 'unfavorite') {
    switch (kind) {
      case 'listing':
        return action === 'favorite'
          ? this.listingService.favorite({ id } as IListing)
          : this.listingService.unfavorite({ id } as IListing);
      case 'boondocking':
        return action === 'favorite'
          ? this.boondockService.favorite({ id } as IBoondock)
          : this.boondockService.unfavorite({ id } as IBoondock);
      case 'poi':
        return action === 'favorite'
          ? this.activityService.favorite({ id: Number(id) } as IActivityLocation)
          : this.activityService.unfavorite({ id: Number(id) } as IActivityLocation);
    }
  }
}

function mapUserFavorites(user: IUser | undefined): IFavorite[] {
  if (!user) return [];
  const listing = (user.listingFavorites ?? [])
    .filter(f => f.listingId != null)
    .map(f => ({ kind: 'listing' as const, id: f.listingId, row: f.id ?? 0 }));
  const boondocking = (user.boondockFavorites ?? [])
    .filter(f => f.boondockId != null)
    .map(f => ({ kind: 'boondocking' as const, id: f.boondockId, row: f.id ?? 0 }));
  const poi = (user.poiFavorites ?? [])
    .filter(f => f.poiId != null)
    .map(f => ({ kind: 'poi' as const, id: String(f.poiId), row: f.id ?? 0 }));

  return [...listing, ...boondocking, ...poi]
    .sort((a, b) => b.row - a.row) // newest-first: server orders each kind's rows by id ASC
    .map(({ kind, id }) => ({ kind, id, savedAt: '' }));
}
